import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { debounce, pick, snakeCase, uniqBy } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import AppliedFiltersChips, {
  AppliedFiltersChipsCollectedData,
} from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { OrderDispatchQuery } from '~pages/Dispatch/components/filters/orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import useProjectsFilters from '../../hooks/useProjectsFilters';
import ProjectsQueryContext from '../../ProjectsQueryContext';
import {
  useCollaboratorsQuery,
  useDepartmentsQuery,
  useDropoffSitesQuery,
  useForemenQuery,
  useMaterialsQuery,
  usePickupSitesQuery,
  useRequestersQuery,
  useSalespersonsQuery,
} from './projectsFiltersQueries';
import {
  projectsFilterKeyBySelectedFilter,
  ProjectsSelectedFilter,
} from './projectsFilterTypes';

export default function ProjectsFilters() {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<ProjectsSelectedFilter>();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const { filters, applyFilters, removeAllFilters } = useContext(ProjectsQueryContext);

  const {
    appliedFilterChipsCollectedData,
    updateAvailableOptions,
    updateAppliedFilterChipsCollectedData,
  } = useProjectsFilters();

  const [searchQuery, setSearchQuery] = useState<{
    input: string;
    debounced: string;
  }>({
    input: '',
    debounced: '',
  });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const collaboratorsQuery = useCollaboratorsQuery(companyId);
  const departmentsQuery = useDepartmentsQuery(companyId);
  const dropoffSitesQuery = useDropoffSitesQuery(companyId);
  const foremenQuery = useForemenQuery(companyId);
  const materialsQuery = useMaterialsQuery(companyId);
  const pickupSitesQuery = usePickupSitesQuery(companyId);
  const requestersQuery = useRequestersQuery(companyId);
  const salespersonsQuery = useSalespersonsQuery(companyId);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const queries: Record<ProjectsSelectedFilter, OrderDispatchQuery> = {
      [ProjectsSelectedFilter.COLLABORATORS]: collaboratorsQuery,
      [ProjectsSelectedFilter.DEPARTMENTS]: departmentsQuery,
      [ProjectsSelectedFilter.DROPOFF_SITES]: dropoffSitesQuery,
      [ProjectsSelectedFilter.FOREMEN]: foremenQuery,
      [ProjectsSelectedFilter.MATERIALS]: materialsQuery,
      [ProjectsSelectedFilter.PICKUP_SITES]: pickupSitesQuery,
      [ProjectsSelectedFilter.REQUESTERS]: requestersQuery,
      [ProjectsSelectedFilter.SALESPERSONS]: salespersonsQuery,
    };

    return queries[selectedFilter];
  }, [
    selectedFilter,
    collaboratorsQuery,
    departmentsQuery,
    dropoffSitesQuery,
    foremenQuery,
    materialsQuery,
    pickupSitesQuery,
    requestersQuery,
    salespersonsQuery,
  ]);

  const searchValueChangeCallback = useMemo(() => {
    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, []);

  const filterChipsData = useMemo(() => {
    const appliedFilters = pick(
      filters,
      Object.values(projectsFilterKeyBySelectedFilter),
    );

    const collectedData = Object.entries(appliedFilterChipsCollectedData).reduce(
      (obj, [key, collectedValues]) => {
        const filter = projectsFilterKeyBySelectedFilter[key as ProjectsSelectedFilter];

        obj[filter] = collectedValues;

        return obj;
      },
      {} as AppliedFiltersChipsCollectedData,
    );

    return { appliedFilters, collectedData };
  }, [JSON.stringify(filters), appliedFilterChipsCollectedData]);

  const hasFiltersApplied = useMemo(() => {
    const { appliedFilters } = filterChipsData;

    return Object.values(appliedFilters).filter((value) => Boolean(value)).length > 0;
  }, [JSON.stringify(filterChipsData.appliedFilters)]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = projectsFilterKeyBySelectedFilter[selectedFilter];
    const options = filters[key];

    return options ?? [];
  }, [filters, selectedFilter]);

  const handleSelectedOptionsChange = useCallback(
    (selectedOptions: string[]) => {
      if (selectedFilter) {
        const key = projectsFilterKeyBySelectedFilter[selectedFilter];
        const options = selectedOptions.length > 0 ? selectedOptions : undefined;

        applyFilters({ [key]: options });
      }
    },
    [selectedFilter, applyFilters],
  );

  const fetchMoreCallback = useMemo(() => {
    return activeQuery?.fetchMore;
  }, [activeQuery?.fetchMore]);

  const handleRemoveAllFilters = useCallback(
    (filtersToRemove: string | string[]) => {
      const filtersArray = Array.isArray(filtersToRemove)
        ? filtersToRemove
        : [filtersToRemove];

      removeAllFilters(filtersArray);
    },
    [removeAllFilters],
  );

  const updateAvailableOptionsAndFilterChips = useCallback(
    (key: ProjectsSelectedFilter, data: FilterMenuOption[]) => {
      updateAvailableOptions((state) => {
        return {
          ...state,
          [key]: uniqBy([...state[key], ...data], (i) => i.value),
        };
      });

      updateAppliedFilterChipsCollectedData(key, data);
    },
    [],
  );

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    if (selectedFilter && activeQuery?.data) {
      updateAvailableOptionsAndFilterChips(selectedFilter, activeQuery?.data);
    }
  }, [updateAvailableOptionsAndFilterChips, selectedFilter, activeQuery?.data]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
      setSearchQuery({ input: '', debounced: '' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      collaboratorsQuery.clear();
      departmentsQuery.clear();
      dropoffSitesQuery.clear();
      foremenQuery.clear();
      materialsQuery.clear();
      pickupSitesQuery.clear();
      requestersQuery.clear();
      salespersonsQuery.clear();
    }
  }, [
    selectedFilter,
    collaboratorsQuery.clear,
    departmentsQuery.clear,
    dropoffSitesQuery.clear,
    foremenQuery.clear,
    materialsQuery.clear,
    pickupSitesQuery.clear,
    requestersQuery.clear,
    salespersonsQuery.clear,
  ]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={{
              [`&.${buttonClasses.root}`]: {
                backgroundColor: theme.semantic.surfaceRaised,
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                ...(hasFiltersApplied
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            }}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            searchValue={searchQuery.input}
            onSearchValueChange={searchValueChangeCallback}
            loadingReason={activeQuery?.loadingReason}
            selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', { entity: t(`projects.filters.${snakeCase(selectedFilter)}`) })}`}
            options={activeQuery?.data ?? []}
            selectedOptions={selectedOptions}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            onFetchMore={fetchMoreCallback}
          />
        )}
        {!selectedFilter &&
          Object.values(ProjectsSelectedFilter).map((filter) => (
            <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                  {t(`projects.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {(filters[projectsFilterKeyBySelectedFilter[filter]] ?? []).length >
                    0 && (
                    <Check
                      sx={{
                        fontSize: '16px',
                        color: theme.brandV2.colors.treadOrange,
                      }}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>
      <AppliedFiltersChips
        {...filterChipsData}
        onRemoveFilter={handleRemoveAllFilters}
        onRemoveAllFilters={handleRemoveAllFilters}
      />
    </Box>
  );
}
